/**
 * igadget address-gate — intercepts checkout attempts from the cart drawer,
 * cart page, and the PDP "Buy It Now" button, routing all through the address
 * validation page first.
 *
 * Two strategies:
 *   1. Cart-based checkout (cart drawer, /cart page): intercept clicks on
 *      a[href*="/checkout"] and button[name="checkout"] via capture-phase
 *      listener, fetch /cart.js, redirect to validation.
 *   2. PDP "Buy It Now": Shopify's <shopify-buy-it-now-button> web component
 *      navigates to /checkout via internal JS that can't be reliably
 *      intercepted. So we HIDE the native dynamic checkout button and inject
 *      our own "Buy It Now" button that routes through the validation page.
 *
 * Product details (title, image, price, variant, quantity) are passed to the
 * validation page via URL params so the customer can see what they're checking
 * out, with a "change product" link back to the source.
 */

(function () {
  'use strict';

  var VALIDATION_PAGE_PATH = '/pages/address-check';

  var path = window.location.pathname;
  if (path.indexOf(VALIDATION_PAGE_PATH) !== -1 || path.indexOf('/checkout') !== -1) {
    return;
  }

  // Bypass for admin testing
  if (window.location.search.indexOf('bypass_address_gate=1') !== -1) {
    sessionStorage.setItem('bypass_address_gate', '1');
  }
  if (sessionStorage.getItem('bypass_address_gate') === '1') {
    return;
  }

  // Encode an array of product items into a URL-safe string for the validation page.
  // Each item: {variant_id, quantity, title, image, price, url}
  function encodeItems(items) {
    return encodeURIComponent(JSON.stringify(items));
  }

  function buildVariantsParam(cart) {
    if (!cart || !cart.items || cart.items.length === 0) return null;
    return cart.items.map(function (item) {
      return item.variant_id + ':' + item.quantity;
    }).join(',');
  }

  function buildCartItems(cart) {
    if (!cart || !cart.items) return [];
    return cart.items.map(function (item) {
      return {
        variant_id: item.variant_id,
        quantity: item.quantity,
        title: item.product_title + (item.variant_title ? ' — ' + item.variant_title : ''),
        image: item.image,
        price: item.price / 100, // Shopify cart prices are in cents
        url: item.url,
        source: 'cart'
      };
    });
  }

  function redirectToValidation(variantsParam, items) {
    if (!variantsParam) return false;
    var url = VALIDATION_PAGE_PATH + '?variants=' + encodeURIComponent(variantsParam);
    if (items && items.length) {
      url += '&items=' + encodeItems(items);
    }
    window.location.href = url;
    return true;
  }

  function fetchCartAndRedirect(fallback) {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var variants = buildVariantsParam(cart);
        var items = buildCartItems(cart);
        if (!redirectToValidation(variants, items)) fallback();
      })
      .catch(function () { fallback(); });
  }

  // PDP: extract the selected variant + product details from the product page.
  // Returns {variant_id, quantity, title, image, price, url, source}
  function getSelectedProductFromPDP() {
    var form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return null;
    var variantInput = form.querySelector('input[name="id"]');
    if (!variantInput) return null;
    var variantId = variantInput.value;
    if (!variantId) return null;
    var qtyInput = form.querySelector('input[name="quantity"]');
    var qty = (qtyInput && qtyInput.value) ? parseInt(qtyInput.value, 10) : 1;
    if (isNaN(qty) || qty < 1) qty = 1;

    // Product title: from <meta property="og:title"> or the <h1> in product info
    var title = '';
    var ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) title = ogTitle.getAttribute('content');
    if (!title) {
      var h1 = document.querySelector('h1.product-title, .product__title h1, h1[type="h1"]');
      if (h1) title = h1.textContent.trim();
    }

    // Product image: from <meta property="og:image">
    var image = '';
    var ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) image = ogImage.getAttribute('content');

    // Price: from meta tags. The Sense theme doesn't emit product:price:amount,
    // but it does emit og:price:amount. Try both, plus product:price:amount as
    // a fallback for themes that use the standard Open Graph product tag.
    var price = 0;
    var priceMeta = document.querySelector('meta[property="product:price:amount"]')
                 || document.querySelector('meta[property="og:price:amount"]');
    if (priceMeta) {
      // Strip commas (meta content may be "2,099.00") before parsing.
      price = parseFloat(priceMeta.getAttribute('content').replace(/,/g, '')) || 0;
    }
    // Fallback: scrape the on-page sale price (first .price-item--sale in the
    // main price block, not related products). Strip the peso sign and commas.
    if (!price) {
      var priceBlock = document.querySelector('.price__sale .price-item--sale, .price__regular .price-item--regular');
      if (priceBlock) {
        var txt = priceBlock.textContent.replace(/[₱,]/g, '').trim();
        var parsed = parseFloat(txt);
        if (!isNaN(parsed)) price = parsed;
      }
    }

    // Selected variant title (for products with options like color/size)
    var variantTitle = '';
    // Some themes expose the selected variant via a data attribute or JSON
    var variantJson = document.querySelector('[data-variant]');
    if (variantJson) {
      try {
        var v = JSON.parse(variantJson.getAttribute('data-variant'));
        variantTitle = v.public_title || v.title || '';
      } catch (e) {}
    }
    // Fallback: read from the variant select elements
    if (!variantTitle) {
      var selectedOptions = Array.from(form.querySelectorAll('select[data-index] option:checked, input[data-index]:checked'))
        .map(function (el) { return el.textContent.trim() || el.value; })
        .filter(function (v) { return v; });
      if (selectedOptions.length) variantTitle = selectedOptions.join(' / ');
    }

    var fullTitle = title;
    if (variantTitle && variantTitle !== 'Default Title') fullTitle = title + ' — ' + variantTitle;

    return {
      variant_id: variantId,
      quantity: qty,
      title: fullTitle,
      image: image,
      price: price,
      url: window.location.pathname,
      source: 'pdp'
    };
  }

  // === Strategy 1: capture-phase click listener for cart-based checkout ===
  document.addEventListener('click', function (e) {
    var el = e.target;
    while (el && el !== document) {
      var tag = el.tagName;
      var href = el.getAttribute && el.getAttribute('href');
      var cls = el.className || '';
      var clsStr = typeof cls === 'string' ? cls : (cls.baseVal || '');

      var isCheckoutLink = tag === 'A' && href && href.indexOf('/checkout') !== -1;
      var isDynamicBuyNow = tag === 'BUTTON' && clsStr.indexOf('shopify-payment-button__button') !== -1;
      var isCartSubmit = (tag === 'BUTTON' || tag === 'INPUT') &&
        (el.getAttribute('name') === 'checkout' ||
         (el.getAttribute('type') === 'submit' && el.closest('form[action*="/checkout"]')));

      if (isCheckoutLink || isDynamicBuyNow || isCartSubmit) {
        e.preventDefault();
        e.stopPropagation();

        if (path.indexOf('/products/') !== -1) {
          var product = getSelectedProductFromPDP();
          if (product) {
            var variantsParam = product.variant_id + ':' + product.quantity;
            redirectToValidation(variantsParam, [product]);
            return;
          }
        }
        fetchCartAndRedirect(function () {
          if (isCheckoutLink) window.location.href = href;
          else if (isCartSubmit) { var form = el.closest('form'); if (form) form.submit(); }
          else el.click();
        });
        return;
      }
      el = el.parentElement;
    }
  }, true);

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (form && form.getAttribute('action') && form.getAttribute('action').indexOf('/checkout') !== -1) {
      e.preventDefault();
      e.stopPropagation();
      if (path.indexOf('/products/') !== -1) {
        var product = getSelectedProductFromPDP();
        if (product) {
          redirectToValidation(product.variant_id + ':' + product.quantity, [product]);
          return;
        }
      }
      fetchCartAndRedirect(function () { form.submit(); });
    }
  }, true);

  // === Strategy 2: on PDP, replace the dynamic checkout button with our own ===
  if (path.indexOf('/products/') !== -1) {
    function replaceDynamicCheckout() {
      var container = document.querySelector('.shopify-payment-button, .dynamic-checkout-buttons');
      if (!container) return false;
      if (container.querySelector('.igadget-buy-now-btn')) return true;
      container.style.display = 'none';

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'igadget-buy-now-btn';
      btn.textContent = 'Buy it now';
      btn.style.cssText = [
        'display:block', 'width:100%', 'padding:14px 20px',
        'font-size:16px', 'font-weight:600', 'color:#fff', 'background:#000',
        'border:none', 'border-radius:40px', 'cursor:pointer',
        'margin-top:12px', 'text-transform:uppercase', 'letter-spacing:0.08em'
      ].join(';');

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var product = getSelectedProductFromPDP();
        if (product) {
          redirectToValidation(product.variant_id + ':' + product.quantity, [product]);
        } else {
          fetchCartAndRedirect(function () { alert('Please select a product option first.'); });
        }
      });

      container.parentElement.appendChild(btn);
      return true;
    }

    if (!replaceDynamicCheckout()) {
      var observer = new MutationObserver(function () {
        if (replaceDynamicCheckout()) observer.disconnect();
      });
      observer.observe(document.body, { childList: true, subtree: true });
      setTimeout(function () { observer.disconnect(); }, 10000);
    }
  }
})();
